import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { formatRelativeTime } from '@/utils/date';
import ApperIcon from '@/components/ApperIcon';
import { StatsSkeleton } from '@/components/PageSkeleton';
import { Metric, PageHeader, Pager, Panel, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import CopyButton from '@/components/admin/CopyButton';
import { BOOL_STYLES, ORDER_STATUS_STYLES } from '@/shared/constants';
import { planSummary } from '@/shared/pricing';
import { idOf, nameOf, rows } from '@/shared/data';
import { MIN_REFERRAL_MONTHS, isPaidLine, normUsername, qualifiesForReferral } from '@/shared/referrals';
import { firstLineOf, referralUrl, shortUrl } from '@/shared/referralLink';

export const route = { path: '/admin/referrals', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'Gift', label: 'Referrals', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 32 };

const PAGE_SIZE = 25;
const DAY = 86400000;

const SHOW = ['live', 'visits', 'referred', 'idle'];
const SHOW_LABELS = {
  live: 'Live links',
  visits: 'Opened at least once',
  referred: 'Referred an order',
  idle: 'Link not live',
};

const ORDER_SELECT = [
  'Name',
  'customer_c',
  'full_name_c',
  'email_c',
  'plan_c',
  'term_c',
  'status_c',
  'activated_at_c',
  'ends_at_c',
  'line_username_c',
  'referred_by_c',
  'referral_counted_c',
  'gift_months_c',
  'CreatedOn',
];

async function loadReferrals() {
  const [ordersRes, visitsRes, customersRes] = await Promise.all([
    sdk.table('orders_c').select(ORDER_SELECT).orderBy('CreatedOn').limit(3000).fetch(),
    sdk
      .table('referral_visits_c')
      .select(['Name', 'landing_c', 'visited_at_c', 'visitor_c'])
      .orderByDesc('visited_at_c')
      .limit(5000)
      .fetch(),
    sdk.table('customers_c').select(['Name', 'email_c', 'referral_code_c', 'referrals_earned_c']).limit(3000).fetch(),
  ]);
  const orders = rows(ordersRes);
  const visits = visitsRes?.success ? (visitsRes.data ?? []) : [];
  const customers = rows(customersRes);
  return build(orders, visits, customers, !visitsRes?.success);
}

function build(orders, visits, customers, visitsFailed) {
  const customerById = new Map(customers.map((c) => [String(c.Id), c]));
  const groups = new Map();
  for (const o of orders) {
    const cid = idOf(o.customer_c);
    const key = cid ? `c:${cid}` : `e:${String(o.email_c || '').trim().toLowerCase()}`;
    if (key === 'e:') continue;
    if (!groups.has(key)) groups.set(key, { key, customerId: cid, orders: [] });
    groups.get(key).orders.push(o);
  }

  const visitsByName = new Map();
  for (const v of visits) {
    const n = normUsername(v.Name);
    if (!n) continue;
    if (!visitsByName.has(n)) visitsByName.set(n, []);
    visitsByName.get(n).push(v);
  }

  const referredByName = new Map();
  const referredOrders = [];
  for (const o of orders) {
    const n = normUsername(o.referred_by_c);
    if (!n) continue;
    referredOrders.push(o);
    if (!referredByName.has(n)) referredByName.set(n, []);
    referredByName.get(n).push(o);
  }

  const referrers = [];
  const ownerOfName = new Map();
  for (const g of groups.values()) {
    const first = firstLineOf(g.orders);
    if (!first) continue;
    const customer = g.customerId ? customerById.get(String(g.customerId)) : null;
    const username = normUsername(first.line_username_c);
    const names = new Set(g.orders.map((o) => normUsername(o.line_username_c)).filter(Boolean));
    if (customer?.referral_code_c) names.add(normUsername(customer.referral_code_c));
    const myVisits = [...names].flatMap((n) => visitsByName.get(n) ?? []);
    const myReferred = [...names].flatMap((n) => referredByName.get(n) ?? []);
    const lastVisit = myVisits.reduce((acc, v) => (v.visited_at_c > acc ? v.visited_at_c : acc), '');
    const row = {
      Id: g.key,
      customerId: g.customerId,
      name: nameOf(first.customer_c) || customer?.Name || first.full_name_c || first.email_c || 'Buyer',
      email: customer?.email_c || first.email_c || '',
      username,
      url: referralUrl(username),
      live: g.orders.some((o) => normUsername(o.line_username_c) === username && isPaidLine(o)),
      visits: myVisits.length,
      visitors: new Set(myVisits.map((v) => v.visitor_c || v.Id)).size,
      lastVisit,
      referred: myReferred.length,
      qualifying: myReferred.filter((o) => qualifiesForReferral(o)).length,
      counted: myReferred.filter((o) => o.referral_counted_c).length,
      earned: Number(customer?.referrals_earned_c || 0),
      owed: g.orders.reduce((n, o) => n + (Number(o.gift_months_c) || 0), 0),
    };
    for (const n of names) ownerOfName.set(n, row);
    referrers.push(row);
  }

  const since = Date.now() - 30 * DAY;
  const totals = {
    visits: visits.length,
    visits30: visits.filter((v) => new Date(v.visited_at_c).getTime() >= since).length,
    visitors: new Set(visits.map((v) => v.visitor_c || v.Id)).size,
    referred: referredOrders.length,
    counted: referredOrders.filter((o) => o.referral_counted_c).length,
    owed: orders.reduce((n, o) => n + (Number(o.gift_months_c) || 0), 0),
    links: referrers.filter((r) => r.live).length,
  };

  referredOrders.sort((a, b) => String(b.CreatedOn || '').localeCompare(String(a.CreatedOn || '')));

  return {
    referrers,
    totals,
    recentOrders: referredOrders.slice(0, 12).map((o) => ({ ...o, referrer: ownerOfName.get(normUsername(o.referred_by_c)) ?? null })),
    recentVisits: visits.slice(0, 12).map((v) => ({ ...v, referrer: ownerOfName.get(normUsername(v.Name)) ?? null })),
    visitsFailed,
  };
}

function matches(r, q) {
  if (!q) return true;
  const s = q.toLowerCase();
  return [r.name, r.email, r.username].some((v) => String(v || '').toLowerCase().includes(s));
}

export default function AdminReferrals() {
  const [search, setSearch] = useState('');
  const [show, setShow] = useState('all');
  const [page, setPage] = useState(1);
  const q = useDebounced(search.trim());
  const { data, loading, error, run } = useFetch(loadReferrals, []);

  useEffect(() => {
    setPage(1);
  }, [q, show]);

  const filtered = useMemo(() => {
    const list = (data?.referrers ?? []).filter((r) => {
      if (!matches(r, q)) return false;
      if (show === 'live') return r.live;
      if (show === 'visits') return r.visits > 0;
      if (show === 'referred') return r.referred > 0;
      if (show === 'idle') return !r.live;
      return true;
    });
    return list.sort(
      (a, b) =>
        b.referred - a.referred ||
        b.visits - a.visits ||
        String(b.lastVisit).localeCompare(String(a.lastVisit)) ||
        a.name.localeCompare(b.name),
    );
  }, [data, q, show]);

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const t = data?.totals;
  const isFiltered = !!q || show !== 'all';

  const columns = [
    {
      label: 'Buyer',
      render: (r) =>
        r.customerId ? (
          <RowLink to={`/admin/customers/${r.customerId}`} sub={r.email}>
            {r.name}
          </RowLink>
        ) : (
          <span>
            <span className="font-medium">{r.name}</span>
            {r.email && <span className="block text-xs text-muted-foreground">{r.email}</span>}
          </span>
        ),
    },
    {
      label: 'Referral link',
      render: (r) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0">
            <span className="block font-mono text-sm">{r.username}</span>
            <a
              href={r.url}
              target="_blank"
              rel="noreferrer"
              className="block max-w-[16rem] truncate text-xs text-muted-foreground hover:text-primary"
              title={r.url}
            >
              {shortUrl(r.url)}
            </a>
          </span>
          <CopyButton text={r.url} label="Copy" variant="ghost" />
        </div>
      ),
    },
    {
      label: 'Live',
      className: 'hidden md:table-cell',
      render: (r) => <StatusBadge value={r.live ? 'Yes' : 'No'} styles={BOOL_STYLES} />,
    },
    {
      label: 'Visits',
      className: 'tabular-nums',
      render: (r) =>
        r.visits ? (
          <span>
            {r.visits}
            <span className="block text-xs text-muted-foreground">
              {r.visitors} unique · {formatRelativeTime(r.lastVisit)}
            </span>
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: 'Referred',
      className: 'tabular-nums',
      render: (r) =>
        r.referred ? (
          <span>
            {r.referred} order{r.referred === 1 ? '' : 's'}
            <span className="block text-xs text-muted-foreground">
              {r.qualifying} on {MIN_REFERRAL_MONTHS}+ months · {r.counted} counted
            </span>
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: 'Free months',
      className: 'hidden lg:table-cell tabular-nums',
      render: (r) =>
        r.earned || r.owed ? (
          <span>
            {r.earned} earned
            <span className="block text-xs text-muted-foreground">{r.owed} still owed</span>
          </span>
        ) : (
          <Dash />
        ),
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Store"
        title="Referrals"
        description={`Every buyer’s referral link (the username on their first line), how often it’s opened, and the orders it brought in. A referral counts on ${MIN_REFERRAL_MONTHS}+ months once the friend’s line is live.`}
      />

      {loading && data === undefined ? (
        <StatsSkeleton count={4} />
      ) : error ? null : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric
            label="Link visits"
            value={t?.visits ?? 0}
            hint={`${t?.visits30 ?? 0} in the last 30 days · ${t?.visitors ?? 0} unique`}
            icon="MousePointerClick"
          />
          <Metric label="Referred orders" value={t?.referred ?? 0} hint={`${t?.counted ?? 0} counted`} icon="ShoppingCart" tone="text-highlight" />
          <Metric label="Live links" value={t?.links ?? 0} hint="Buyers with an active, paid first line" icon="Link2" tone="text-success" />
          <Metric label="Free months owed" value={t?.owed ?? 0} hint="Still to add to referrers’ lines" icon="Gift" tone="text-warning" />
        </div>
      )}

      {data?.visitsFailed && (
        <p className="mt-4 flex items-center gap-2 text-sm text-warning">
          <ApperIcon name="CircleAlert" size={16} />
          Link visits could not be loaded. Orders and links are still shown.
        </p>
      )}

      <div className="mt-6">
        <Toolbar>
          <SearchBox value={search} onChange={setSearch} placeholder="Search buyer, email or username…" />
          <FilterSelect value={show} onChange={setShow} values={SHOW} labels={SHOW_LABELS} allLabel="All buyers" />
        </Toolbar>

        <ListBody
          loading={loading}
          data={data}
          error={error}
          run={run}
          isEmpty={!filtered.length}
          empty={{
            icon: 'Gift',
            title: isFiltered ? 'No referrers match' : 'No referral links yet',
            description: isFiltered
              ? 'Try another search or filter.'
              : 'A buyer gets a link once their first line has a username.',
            action: isFiltered
              ? () => {
                  setSearch('');
                  setShow('all');
                }
              : undefined,
            actionLabel: isFiltered ? 'Clear filters' : undefined,
          }}
        >
          <DataTable columns={columns} rows={pageRows} />
          <Pager page={page} total={filtered.length} pageSize={PAGE_SIZE} hasMore={page * PAGE_SIZE < filtered.length} onPage={setPage} />
        </ListBody>
      </div>

      {data && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Panel title="Latest referred orders" icon="ShoppingCart" bodyClassName="py-1">
            {data.recentOrders.length ? (
              <ul className="divide-y divide-border">
                {data.recentOrders.map((o) => (
                  <li key={o.Id} className="flex items-start justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <Link to={`/admin/orders/${o.Id}`} className="font-mono text-sm text-primary hover:underline">
                        {o.Name}
                      </Link>
                      <p className="truncate text-sm">
                        {nameOf(o.customer_c) || o.full_name_c || o.email_c} · {planSummary(o)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Used <span className="font-mono text-foreground">{normUsername(o.referred_by_c)}</span>
                        {o.referrer ? ` (${o.referrer.name})` : ' (no matching buyer)'} · {formatRelativeTime(o.CreatedOn)}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <StatusBadge value={o.status_c} styles={ORDER_STATUS_STYLES} />
                      <span className="text-xs text-muted-foreground">
                        {o.referral_counted_c ? 'Counted' : qualifiesForReferral(o) ? 'Not counted yet' : `Under ${MIN_REFERRAL_MONTHS} months`}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-4 text-sm text-muted-foreground">No orders have used a referral yet.</p>
            )}
          </Panel>

          <Panel title="Latest link visits" icon="MousePointerClick" bodyClassName="py-1">
            {data.recentVisits.length ? (
              <ul className="divide-y divide-border">
                {data.recentVisits.map((v) => (
                  <li key={v.Id} className="flex items-start justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="font-mono text-sm">{normUsername(v.Name)}</p>
                      <p className="truncate text-xs text-muted-foreground" title={v.landing_c}>
                        {v.referrer ? v.referrer.name : 'No matching buyer'}
                        {v.landing_c ? ` · ${v.landing_c}` : ''}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">{formatRelativeTime(v.visited_at_c)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-4 text-sm text-muted-foreground">Nobody has opened a referral link yet.</p>
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}
